// Galaxies and the caller's player (04 §4.3).
import { z } from 'zod';
import { idSchema, isoDateTimeSchema } from './common.js';

/** Technical limits for commander names; unique per galaxy, case-insensitive (03 §3.4). */
export const DISPLAY_NAME_MIN_LENGTH = 3;
export const DISPLAY_NAME_MAX_LENGTH = 20;

export const displayNameSchema = z
  .string()
  .trim()
  .min(DISPLAY_NAME_MIN_LENGTH)
  .max(DISPLAY_NAME_MAX_LENGTH)
  .regex(/^[\p{L}\p{N}][\p{L}\p{N} ._-]*$/u, 'letters, digits, spaces and . _ - only')
  .refine((name) => !name.includes('  '), 'no double spaces');

export const galaxyListItemSchema = z.strictObject({
  id: idSchema,
  name: z.string(),
  status: z.enum(['active', 'archived']),
  playerCount: z.number().int().nonnegative(),
  roundIntervalS: z.number().int().positive(),
  speedPct: z.number().int().positive(),
  joined: z.boolean(),
});
export type GalaxyListItem = z.infer<typeof galaxyListItemSchema>;
export const galaxyListResponseSchema = z.strictObject({ items: z.array(galaxyListItemSchema) });

export const joinGalaxyRequestSchema = z.strictObject({ displayName: displayNameSchema });
export type JoinGalaxyRequest = z.infer<typeof joinGalaxyRequestSchema>;

export const joinGalaxyResponseSchema = z.strictObject({
  playerId: idSchema,
  homeSystemId: idSchema,
  shieldUntil: isoDateTimeSchema,
});
export type JoinGalaxyResponse = z.infer<typeof joinGalaxyResponseSchema>;

/** The starting missions in their fixed order (G-72); shown one at a time (G-75). */
export const FIRST_GOALS = [
  'upgrade_building',
  'upgrade_storage',
  'build_ship',
  'scout_system',
  'raid_unowned',
  'join_alliance',
  'upgrade_trading_port',
  'start_trade',
  'earn_credits',
  'claim_unowned',
  'capture_system',
] as const;
export type FirstGoal = (typeof FIRST_GOALS)[number];
export const firstGoalsSchema = z.strictObject({
  goals: z.array(z.strictObject({ id: z.enum(FIRST_GOALS), done: z.boolean() })),
  /** Rewards collected so far; the current mission is `goals[collected]` (none once all are). */
  collected: z.number().int().nonnegative(),
  /** Credits each mission pays when collected (G-74). */
  rewardCredits: z.number().int().nonnegative(),
  /** Mission `earn_credits`: Credits from trades completed while it is current (G-73). */
  earnCredits: z.strictObject({
    earned: z.number().int().nonnegative(),
    target: z.number().int().positive(),
  }),
});
export type FirstGoals = z.infer<typeof firstGoalsSchema>;
/** `POST G/me/missions/collect`: names the mission, so a second click cannot collect the next one. */
export const collectMissionRequestSchema = z.strictObject({ goal: z.enum(FIRST_GOALS) });
/** The current mission's reward is paid and the next one shows. */
export const collectMissionResponseSchema = z.strictObject({
  firstGoals: firstGoalsSchema,
  credits: z.number().int().nonnegative(),
});
export type CollectMissionResponse = z.infer<typeof collectMissionResponseSchema>;

export const galaxyMeResponseSchema = z.strictObject({
  playerId: idSchema,
  displayName: z.string(),
  credits: z.number().int().nonnegative(),
  /** The account's Dark Matter (account-wide, G-28), shown in the top bar (P37). */
  darkMatter: z.number().int().nonnegative(),
  artifacts: z.number().int().nonnegative(),
  shieldUntil: isoDateTimeSchema.nullable(),
  tradeUnlockedAt: isoDateTimeSchema,
  allianceRejoinAllowedAt: isoDateTimeSchema.nullable(),
  alliance: z
    .strictObject({
      id: idSchema,
      tag: z.string(),
      rank: z.enum(['overlord', 'warlord', 'commander', 'recruit']),
    })
    .nullable(),
  systems: z.array(z.strictObject({ id: idSchema, name: z.string(), isCitadel: z.boolean() })),
  unreadNotifications: z.number().int().nonnegative(),
  firstGoals: firstGoalsSchema,
});
export type GalaxyMeResponse = z.infer<typeof galaxyMeResponseSchema>;
